/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * How a target detected on a device is recognized again when the same tool calls Speakeasy's MCP gateway. A device signature and a registered OAuth client share no natural join key, so the link is declared here. The three lists are not interchangeable: the first two name credentials Speakeasy verified and can be enforced on, the third names what a client said about itself and is used only to attribute traffic.
 */
export type AiScanTargetGatewayClient = {
  /**
   * Vendor keys from Speakeasy's CIMD client catalog. Vendor-grained: no two targets may claim the same key, or a block on either would silently cover the other.
   */
  cimdVendorKeys: Array<string>;
  /**
   * Names an MCP client reports at initialize. Detection only, never authorization: the value is self-reported and any client can claim any name.
   */
  clientInfoNames: Array<string>;
  /**
   * Client ids matched literally against the caller's verified client_id, or CIMD catalog URLs — including the wildcard patterns — matched against the catalog entry that admitted it. Naming the catalog URL is how a vendor that mints one document per MCP server is still named exactly. No two targets may claim the same entry.
   */
  oauthClientIds: Array<string>;
};

/** @internal */
export const AiScanTargetGatewayClient$inboundSchema: z.ZodMiniType<
  AiScanTargetGatewayClient,
  unknown
> = z.pipe(
  z.object({
    cimd_vendor_keys: z.array(z.string()),
    client_info_names: z.array(z.string()),
    oauth_client_ids: z.array(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "cimd_vendor_keys": "cimdVendorKeys",
      "client_info_names": "clientInfoNames",
      "oauth_client_ids": "oauthClientIds",
    });
  }),
);
/** @internal */
export type AiScanTargetGatewayClient$Outbound = {
  cimd_vendor_keys: Array<string>;
  client_info_names: Array<string>;
  oauth_client_ids: Array<string>;
};

/** @internal */
export const AiScanTargetGatewayClient$outboundSchema: z.ZodMiniType<
  AiScanTargetGatewayClient$Outbound,
  AiScanTargetGatewayClient
> = z.pipe(
  z.object({
    cimdVendorKeys: z.array(z.string()),
    clientInfoNames: z.array(z.string()),
    oauthClientIds: z.array(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      cimdVendorKeys: "cimd_vendor_keys",
      clientInfoNames: "client_info_names",
      oauthClientIds: "oauth_client_ids",
    });
  }),
);

export function aiScanTargetGatewayClientToJSON(
  aiScanTargetGatewayClient: AiScanTargetGatewayClient,
): string {
  return JSON.stringify(
    AiScanTargetGatewayClient$outboundSchema.parse(aiScanTargetGatewayClient),
  );
}
export function aiScanTargetGatewayClientFromJSON(
  jsonString: string,
): SafeParseResult<AiScanTargetGatewayClient, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AiScanTargetGatewayClient$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AiScanTargetGatewayClient' from JSON`,
  );
}
